import type { GridColDef, GridRenderCellParams, GridRenderEditCellParams } from '@mui/x-data-grid-pro'
import React from 'react'

import { BooleanEditor } from './editors/BooleanEditor'
import { MultiSelectEditor } from './editors/MultiSelectEditor'
import { NumberEditor } from './editors/NumberEditor'
import { getEditComponent } from './editors/registry'
import { SelectEditor } from './editors/SelectEditor'
import { TextEditor } from './editors/TextEditor'
import { renderFilter } from './filters/renderFilter'
import { TruncateLinkCell } from './TruncateLinkCell'
import type { GridColumn, GridFilterDefinition, GridInlineEditConfig, GridInlineField, GridRow } from './types'

const muiTypeFromGridType: Record<GridColumn['type'], GridColDef['type']> = {
	string: 'string',
	text: 'string',
	number: 'number',
	date: 'date',
	datetime: 'dateTime',
	boolean: 'boolean',
	custom: 'string',
}

const muiAlignFromGridAlign: Record<GridColumn['align'], GridColDef['align']> = {
	left: 'left',
	center: 'center',
	right: 'right',
}

const renderServer = (params: GridRenderCellParams): React.ReactElement => {
	const row = params.row as GridRow
	const html = row.__rendered?.[params.field] ?? ''
	return <span dangerouslySetInnerHTML={{ __html: html }} />
}

const renderJs = (
	formatters: NonNullable<typeof window.__gridFormatters>[string] | undefined,
	field: string,
): GridColDef['renderCell'] => {
	const fn = formatters?.[field]
	if (typeof fn !== 'function') {
		return undefined
	}
	return (params: GridRenderCellParams): React.ReactElement => {
		const row = params.row as GridRow
		return <span dangerouslySetInnerHTML={{ __html: fn(params.value, row) }} />
	}
}

const renderTextCell = (params: GridRenderCellParams): React.ReactElement => (
	<TruncateLinkCell value={params.value} row={params.row as GridRow} />
)

const buildEditCellRenderer = (field: GridInlineField): GridColDef['renderEditCell'] => {
	const { kind, componentKey, options } = field.editor

	if (kind === 'custom') {
		if (componentKey === null) {
			return () => <span>Missing component key</span>
		}
		const Custom = getEditComponent(componentKey)
		if (Custom === undefined) {
			return () => <span>Unknown editor: {componentKey}</span>
		}
		return (params: GridRenderEditCellParams) => <Custom {...params} componentOptions={options} />
	}

	if (kind === 'select') {
		return (params: GridRenderEditCellParams) => <SelectEditor {...params} componentOptions={options} />
	}

	if (kind === 'multiSelect') {
		return (params: GridRenderEditCellParams) => <MultiSelectEditor {...params} componentOptions={options} />
	}

	if (kind === 'number') {
		return (params: GridRenderEditCellParams) => <NumberEditor {...params} />
	}

	if (kind === 'boolean') {
		return (params: GridRenderEditCellParams) => <BooleanEditor {...params} />
	}

	if (kind === 'text') {
		return (params: GridRenderEditCellParams) => <TextEditor {...params} />
	}

	return () => null
}

type BuildColumnsOptions = {
	gridId: string
	columns: GridColumn[]
	getFilterValue: (field: string) => unknown
	onColumnFilterChange: (field: string, kind: GridFilterDefinition['kind'], value: unknown) => void
	inlineEdit?: GridInlineEditConfig
}

export const buildColumns = ({
	gridId,
	columns,
	getFilterValue,
	onColumnFilterChange,
	inlineEdit,
}: BuildColumnsOptions): GridColDef[] => {
	const jsFormatters = typeof window !== 'undefined' ? window.__gridFormatters?.[gridId] : undefined
	const inlineFieldByName = new Map<string, GridInlineField>((inlineEdit?.fields ?? []).map((f) => [f.name, f]))

	return columns.map<GridColDef>((col) => {
		const base: GridColDef = {
			field: col.field,
			headerName: col.label,
			sortable: col.sortable,
			filterable: col.filterable,
			type: muiTypeFromGridType[col.type] ?? 'string',
			align: muiAlignFromGridAlign[col.align],
			headerAlign: muiAlignFromGridAlign[col.align],
			width: col.width ?? undefined,
			flex: col.width === null ? (col.flex ?? (col.type === 'text' ? 2 : 1)) : undefined,
			hideable: col.hideable,
		}

		if (col.filter !== null) {
			const filterDef = col.filter
			base.renderHeaderFilter = () =>
				renderFilter({
					definition: filterDef,
					value: getFilterValue(col.field),
					onChange: (value) => onColumnFilterChange(col.field, filterDef.kind, value),
				})
		}

		const inlineField = inlineFieldByName.get(col.field)
		if (inlineField !== undefined) {
			base.editable = true
			base.renderEditCell = buildEditCellRenderer(inlineField)
		}

		if (col.renderer === 'server') {
			return { ...base, renderCell: renderServer }
		}
		if (col.renderer === 'js') {
			const renderCell = renderJs(jsFormatters, col.field)
			return renderCell === undefined ? base : { ...base, renderCell }
		}
		if (col.type === 'text') {
			return { ...base, renderCell: renderTextCell }
		}
		return base
	})
}
